<template>
  <modal v-bind="$attrs" v-on="$listeners" :errors="errors" width="1220px">
    <template v-slot:title>
      <h1>{{ $t('organizations.users_section.modals.invite_users.title') }}</h1>
    </template>
    <template v-slot:body>
      <div id="create-class-page">
        <div>
          <add-learners
            ref="addusers"
            v-if="createClassSteps.active >= 1"
            :currentStep="createClassSteps.active"
            :componentStep="1"
            v-on:next-step="handleNextStep"
            :organizationType="organizationType"
            :organizationId="organizationId"
            v-on:update-add-learner="updateBuildingEnrollments"
            :addLearners="buildingEnrollments"
          />
          <hr v-if="createClassSteps.active >= 2" class="line-separate" />
          <assign-license-learners 
            ref="assignlicense" 
            v-if="createClassSteps.active >= 2" 
            :currentStep="createClassSteps.active" 
            :componentStep="2" 
            v-on:next-step="handleNextStep" 
            v-on:update-assign-license="updateBuildingEnrollments" 
            :assignmentLicense.sync="buildingEnrollments"
            :availableClasses="[...avaiClases]"
            :selectedTeam.sync="selectedTeam"
            :shouldShowInviteAll="shouldShowInviteAll"
          >
            <template v-if="isElsaSpeakProduct" v-slot:select_team>
              <el-table-column prop="text" :label="$t('organizations.invite_users.assign_license.table.class')" width="280">
                <template slot-scope="scope">
                  <elsa-select
                    class="select-team select-team"
                    name="selectedTeam"
                    :placeholder="$t($i18nLabelsHelper(currentOrganizationType, 'modals.invite_users.select_group_placeholder'))"
                    :options="avaiClases"
                    :no-data-text="$t($i18nLabelsHelper(currentOrganizationType, 'modals.invite_users.no_group_available'))"
                    v-model="scope.row.selectedTeam"
                  />
                </template>
              </el-table-column>
            </template>
          </assign-license-learners>
        </div>
      </div>
      <el-button @click.native="changeToMultipleMode" type="text" class="swap-mode-btn"> <i class="el-icon-circle-plus-outline" /> {{ $t('organizations.users_section.modals.invite_multiple.add_single_learner') }}</el-button>
    </template>
    <template v-slot:footer>
      <span class="dialog-footer">
        <tertiary-button @click.native="handleCancelClick">
          {{ $t('organization_profile.invite_admin_modal.cancel_button') }}
        </tertiary-button>
        <primary-button @click.native="handleInvite" ref="submit" type="submit" v-loading.fullscreen.lock="fullscreenLoading">
          {{ createClassSteps.active >= 2 ? $t('organization_profile.invite_admin_modal.invite_button') : $t('common.continue') }}
        </primary-button>
      </span>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/common/Modal'
import PrimaryButton from '@/components/common/buttons/PrimaryButton'
import TertiaryButton from '@/components/common/buttons/TertiaryButton'
import ElsaInput from '@/components/common/Input'
import ElsaSelect from '@/components/common/Select'
import { handleSubmitButtonState, isPropEmpty, handleFormValidation } from '@/helpers/formValidationHelper'
import axios from '@/utils/axios'
import { compose, isNil, of, pathOr, reject, map, defaultTo, path, propOr, groupBy } from 'ramda'
import { successMessage, warningMessage } from '@/utils/messages'
import { selectProMemberships } from '@/constants/memberships'
import { mapGetters, mapActions } from 'vuex'
import AssignLicenseLearners from './invite-learners/AssignLicenseLearners'
import AddLearners from './invite-learners/AddLearners'
export default {
  components: {
    Modal,
    PrimaryButton,
    TertiaryButton,
    AssignLicenseLearners,
    AddLearners,
    ElsaSelect
  },
  props: {
    organizationId: {
      type: Number
    },
    organizationType: {},
    multipleMode: {
      type: Boolean
    },
    shouldShowInviteAll: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      buildingEnrollments: {},
      errors: {},
      form: {
        consent: true
      },
      rules: {
        email: [
          {
            type: 'email',
            message: this.$t('organizations.users_section.modals.invite_multiple.email_validation_msg'),
            trigger: 'blur',
            required: true
          }
        ],
        first_name: [
          {
            type: 'string',
            message: this.$t('organizations.users_section.modals.invite_multiple.firstname_validation_msg'),
            trigger: 'blur',
            required: true
          }
        ],
        last_name: [
          {
            type: 'string',
            message: this.$t('organizations.users_section.modals.invite_multiple.lastname_validation_msg'),
            trigger: 'blur',
            required: true
          }
        ]
      },
      fullscreenLoading: false,
      orgMembership: [],
      createClassSteps: {
        active: 1,
        steps: [
          {
            title: this.$t('classes.create_class_flow.details')
          },
          {
            title: this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, 'students'))
          }
        ]
      },
      selectedTeam: this.$route.params.classId ? parseInt(this.$route.params.classId) : undefined
    }
  },
  methods: {
    async handleInvite(e) {
      e.preventDefault()
      if (this.createClassSteps.active === 1) {
        this.createClassSteps.active = 2
        this.$refs.addusers.handleClickContinute()
      } else if (this.createClassSteps.active === 2) {
        this.inviteUsers()
      }
    },
    async inviteUsers() {
      this.fullscreenLoading = true
      const role = 'student'
      const organizationId = this.organizationId
      let users = this.buildingEnrollments.users
      const byGroupIds = groupBy(function (user) {
        return user.selectedTeam
      })(users)
      const consent = this.buildingEnrollments.consent

      for (let i = 0; i < Object.keys(byGroupIds).length; i++) {
        var selectedTeam = Object.keys(byGroupIds)[i]
        var students = byGroupIds[selectedTeam]
        const studentsEmails = students.map((student) => student.text)
        const studentsFirstNames = students.map((student) => student.first_name)
        const studentsLastName = students.map((student) => student.last_name)
        const packages = students.map((student) => (student.package ? student.package : null))
        const plan_ids = students.map((student) => (student.plan_id ? student.plan_id : null));

        let payload = reject(isNil)({
          organization: organizationId,

          role,
          emails: studentsEmails,
          first_names: studentsFirstNames,
          last_names: studentsLastName,
          auto_join: consent ? 1 : 0,
          packages: packages,
          plan_ids: plan_ids
        })
        if (selectedTeam && selectedTeam !== 'undefined' && selectedTeam !== 'null') {
          payload = { ...payload, group: selectedTeam }
        }
        try {
          await this.$axios.post('/v2/accounts/invite_learner/', payload)
        } catch {
          // do nothing
        }
      }

      this.fullscreenLoading = false
      successMessage(this.$t('organization_profile.invite_admin_modal.invite_success'))(this)
      this.resetModal()
      this.$emit('update-pending')
      this.$emit('on-invited')
      try {
        this.$gtag.event(`${document.title}-learner-invite`, {
          category: `Send-Invite`,
          label: `Add Learners`
        })
      } catch (error) {
        error
      }
    },
    handleCancelClick() {
      this.resetModal()
    },
    onFormChange() {},
    handleError(error) {
      this.errors = error
    },
    resetModal() {
      this.errors = {}
      this.buildingEnrollments = {}
      this.$refs.addusers.resetModal()
      if (this.$refs.assignlicense) {
        this.$refs.assignlicense.resetModal()
      }

      this.createClassSteps.active = 1
      this.$emit('closed')
    },
    ...mapActions('classes', ['getClasses']),
    getOrganizationContracts() {
      let url = `v1/checkouts/${this.organizationId}/payments`
      return axios.get(url).then((response) => {
        const payments = response.data;
        if (payments.length > 0) {
          const plan_details = payments[payments.length - 1].plan_details
          plan_details.forEach((element) => {
            this.orgMembership.push(element.package)
          })
        }
      })
    },
    changeToMultipleMode() {
      this.$emit('update:inviteMultipleMode', false)
    },
    handleNextStep(activeStep, options = {}) {
      // const activeStep = this.createClassSteps.active;
      if (options.mode === 'file-import') {
        this.uploadId = options.uploadId
        this.inviteMode = options.mode
      }
    },
    updateBuildingEnrollments(updatedEnroll) {
      this.buildingEnrollments = {
        ...this.buildingEnrollments,
        ...updatedEnroll
      }
    }
  },
  computed: {
    availableMemberships() {
      return selectProMemberships.filter((e) => this.orgMembership.includes(e.value))
    },
    organizationName() {
      const organizationName = compose(defaultTo(this.$store.getters['organization/currentOrganization'].name), path(['organizationData', 'name']))(this)

      return organizationName
    },
    avaiClases() {
      const availClasses = []
      this.classes.forEach((klass) => {
        availClasses.push({
          label: klass.name,
          value: klass.id
        })
      })
      availClasses.unshift({
        label: this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, 'modals.invite_users.no_class_needed'))
      })
      return availClasses
    },
    ...mapGetters({
      classes: 'classes/getAllClasses',
      currentOrganizationType: 'organization/currentOrganizationType',
      isElsaSpeakProduct: 'organization/isElsaSpeakProduct',
    })
  },
  mounted() {
    try {
      this.getClasses()
      this.getOrganizationContracts()
    } catch {
      return warningMessage(this.$t('common.generic_error_message'))(this)
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        return this.onFormChange()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.elsa-flat-button.el-button {
  padding: 0;
  margin: 0;
}

::v-deep .el-form .el-form-item .el-form-item__label {
  text-transform: capitalize;
}

.invite-desc {
  font-size: 16px;
  line-height: 24px;
  font-weight: normal;
  color: #121131;
  opacity: 0.8;
}

.auth-form {
  .form-row {
    display: flex;

    .first-name,
    .last-name,
    .select-team,
    .select-membership {
      width: 50%;
    }

    .first-name,
    .select-team {
      margin-right: 30px;
    }
  }
}

#swap-mode {
  flex-direction: row !important;
  padding-bottom: 0px;
}

::v-deep .base-create-header {
  padding-bottom: 34px;
}

.new-team {
  width: 100%;
}
#create-class-page {

  padding-bottom: 25px;
  @media only screen and (max-width: 768px) {
    margin: 0 30px;
  }
  h1 {
    border: 0;
  }
  .class-create-steps {
    margin-top: 25px;
  }
  .class-create-main {
    background-color: white;
    padding: 40px 0px;
  }
  ::v-deep .base-create-header {
    padding-bottom: 34px;
  }
  ::v-deep .circle-step {
    border: 1px solid #e6e6e6;
    box-sizing: border-box;
    border-radius: 23px;
    width: 40px;
    height: 40px;
    display: inline-flex;
    justify-content: center;
    padding-top: 8px;
    margin-right: 16px;
  }
}

.success-info {
  .success {
    font-weight: bold;
    font-size: 22px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #46c779;
    line-height: 30px;
  }
  p {
    margin-top: 20px;
    font-weight: 300;
    font-size: 18px;
    color: #546873;
    margin: 0;
  }
}
::v-deep .create-class-com-disable {
  opacity: 0.5;
}
.line-separate {
  border: 1px solid#E6E6E6;
  margin-bottom: 32px;
}

.swap-mode-btn {
  padding-bottom: 0px !important;
}

.dialog-footer {
  width: 515px !important;
  margin: auto;
}
.select-team {
  margin-bottom: 0px !important;
}
</style>
